import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  message,
  Typography,
  Row,
  Col,
  Breadcrumb,
  Divider
} from 'antd';
import { SaveOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import customerJourneyService from '../../services/customerJourneyService';
import JourneyNodeManager from './components/JourneyNodeManager';
import { useFixedFooter, useFixedFooterPlaceholder } from '../../hooks/useFixedFooter';

const { Title } = Typography;
const { TextArea } = Input;

// 生成旅程编码
const generateJourneyCode = () => {
  return 'JOURNEY_' + Date.now().toString().slice(-8);
};

// 生成阶段编码
const generateNodeCode = () => {
  return 'NODE_' + Date.now().toString().slice(-8) + '_' + Math.random().toString(36).substr(2, 4).toUpperCase();
};

const CustomerJourneyCreate = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // 获取路径参数，如果有id则为编辑模式
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [nodes, setNodes] = useState([]);
  
  // 判断是否为编辑模式
  const isEditMode = !!id;

  // 固定底部栏样式
  const fixedFooterStyle = useFixedFooter();
  const placeholderStyle = useFixedFooterPlaceholder();
  
  // 加载旅程数据（编辑模式）
  useEffect(() => {
    if (isEditMode) {
      loadJourneyData();
    }
  }, [id, isEditMode]);
  
  const loadJourneyData = async () => {
    try {
      setInitialLoading(true);
      const response = await customerJourneyService.getJourneyWithNodes(id);
      
      if (response.data.success) {
        const journeyData = response.data.data;
        
        // 填充表单数据
        form.setFieldsValue({
          journeyName: journeyData.journeyName,
          description: journeyData.description || ''
        });
        
        // 设置节点数据
        if (journeyData.nodes && journeyData.nodes.length > 0) {
          setNodes(journeyData.nodes.map(node => ({
            id: node.id,
            nodeCode: node.nodeCode,
            nodeName: node.nodeName,
            description: node.description || '',
            nodeOrder: node.nodeOrder
          })));
        }
      }
    } catch (error) {
      console.error('加载旅程数据失败:', error);
      message.error('加载旅程数据失败');
    } finally {
      setInitialLoading(false);
    }
  };

  // 添加阶段
  const handleAddNode = () => {
    const newNode = {
      id: Date.now(), // 临时ID
      nodeCode: generateNodeCode(), // 自动生成阶段编码
      nodeName: '',
      nodeOrder: nodes.length + 1
    };
    setNodes([...nodes, newNode]);
  };

  // 删除阶段
  const handleDeleteNode = (nodeId) => {
    const updatedNodes = nodes.filter(node => node.id !== nodeId);
    // 重新排序
    updatedNodes.forEach((node, index) => {
      node.nodeOrder = index + 1;
    });
    setNodes(updatedNodes);
  };

  // 更新阶段
  const handleUpdateNode = (nodeId, updatedNode) => {
    setNodes(prevNodes => 
      prevNodes.map(node => 
        node.id === nodeId ? { ...node, ...updatedNode } : node
      )
    );
  };

  // 移动阶段
  const handleMoveNode = (fromIndex, toIndex) => {
    const updatedNodes = [...nodes];
    const [movedNode] = updatedNodes.splice(fromIndex, 1);
    updatedNodes.splice(toIndex, 0, movedNode);
    
    // 重新排序
    updatedNodes.forEach((node, index) => {
      node.nodeOrder = index + 1;
    });
    
    setNodes(updatedNodes);
  };

  // 提交表单
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // 验证阶段数据
      if (nodes.length === 0) {
        message.warning('请至少添加一个旅程阶段');
        return;
      }

      // 检查阶段名称是否为空
      const hasEmptyName = nodes.some(node => !node.nodeName.trim());
      if (hasEmptyName) {
        message.error('请填写所有阶段的名称');
        return;
      }

      const requestData = {
        journeyName: values.journeyName.trim(),
        description: values.description?.trim() || '',
        syncToWework: true, // 固定为true
        nodes: nodes.map(node => ({
          id: node.id && node.id > 1000000000000 ? null : node.id, // 如果是临时ID（时间戳），则设为null表示新增
          nodeCode: node.nodeCode || generateNodeCode(), // 新节点生成编码，已有节点保持原编码
          nodeName: node.nodeName.trim(),
          description: '', // 不再使用阶段描述
          nodeOrder: node.nodeOrder
        }))
      };

      // 编辑模式不需要journeyCode，创建模式需要
      if (!isEditMode) {
        requestData.journeyCode = generateJourneyCode();
      }

      const response = isEditMode 
        ? await customerJourneyService.updateJourney(id, requestData)
        : await customerJourneyService.createJourney(requestData);

      if (response.data.success) {
        message.success(isEditMode ? '旅程更新成功' : '旅程创建成功');
        navigate('/customer-journey');
      }
    } catch (error) {
      console.error(`${isEditMode ? '更新' : '创建'}旅程失败:`, error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error(`${isEditMode ? '更新' : '创建'}旅程失败`);
      }
    } finally {
      setLoading(false);
    }
  };


  // 编辑模式下显示加载状态
  if (isEditMode && initialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>加载旅程数据中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-journey-create">
      {/* 页面头部 */}
      <div>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Breadcrumb>
              <Breadcrumb.Item onClick={() => navigate('/customer-journey')}>
                <Title level={5} style={{ cursor: 'pointer', color: '#1677ff', margin: 0 }}>客户旅程</Title>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Title level={5} style={{ margin: 0 }}>
                  {isEditMode ? '编辑客户旅程' : '创建客户旅程'}
                </Title>
              </Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        </Row>
      </div>

      {/* 主要内容区域 */}
      <div className="journey-content">
        {/* 基本信息 */}
        <Card title="基本信息" style={{ marginBottom: 24 }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              syncToWework: true
            }}
          >
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  name="journeyName"
                  label="旅程名称"
                  rules={[
                    { required: true, message: '请输入旅程名称' },
                    { max: 100, message: '旅程名称不能超过100个字符' }
                  ]}
                >
                  <Input placeholder="请输入旅程名称" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="description"
                  label="旅程描述"
                  rules={[
                    { max: 1000, message: '旅程描述不能超过1000个字符' }
                  ]}
                >
                  <TextArea
                    rows={3}
                    placeholder="请输入旅程描述（可选）"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        {/* 旅程阶段 */}
        <Card
          title="旅程阶段"
          style={{ marginBottom: 24 }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNode}
            style={{ marginBottom: 16 }}
          >
            添加阶段
          </Button>
          
          <JourneyNodeManager
            nodes={nodes}
            onUpdateNode={handleUpdateNode}
            onDeleteNode={handleDeleteNode}
            onMoveNode={handleMoveNode}
            editable={true}
          />
          
          {/* 在阶段列表下方添加一个添加阶段按钮 */}
          {nodes.length > 0 && (
            <Button
              type="default"
              icon={<PlusOutlined />}
              onClick={handleAddNode}
              style={{ 
                marginTop: 16, 
                width: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}
            >
              添加阶段
            </Button>
          )}
        </Card>
      </div>

      {/* 为固定按钮栏预留空间 */}
      <div style={placeholderStyle}></div>

      {/* 固定底部按钮栏 */}
      <div style={fixedFooterStyle}>
        <Space>
          <Button size="middle" onClick={() => navigate('/customer-journey')}>
            取消
          </Button>
          <Button
            type="primary"
            onClick={form.submit}
            loading={loading}
            size="middle"
            icon={<SaveOutlined />}
          >
            {isEditMode ? '保存旅程' : '创建旅程'}
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default CustomerJourneyCreate;